import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, HelpCircle, ChevronDown, ChevronUp, Gamepad2, Trophy, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-border rounded-lg overflow-hidden">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between bg-card hover:bg-muted/50 transition-colors text-left"
    >
      <span className="font-medium pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="px-6 py-4 bg-muted/30 text-muted-foreground leading-relaxed">
            {answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqCategories = [
    {
      icon: Gamepad2,
      title: 'Gameplay',
      faqs: [
        {
          question: 'How do I play JailBirds?',
          answer: "It's simple! You'll see a real mugshot and need to guess which criminal charge belongs to that person. Choose from four options, and you'll earn points for correct answers. Each game has 11 regular questions plus a bonus round."
        },
        {
          question: 'How many questions are in each game?',
          answer: 'Each game consists of 11 regular questions followed by a special JailBird Bonus question. The bonus question gives you a chance to earn extra points with a time-based multiplier!'
        },
        {
          question: 'What is the JailBird Bonus round?',
          answer: 'The JailBird Bonus is a special 12th question where you can earn bonus points. The faster you answer correctly, the higher your multiplier! Answer within 5 seconds for maximum bonus points.'
        },
        {
          question: 'Can I skip a question?',
          answer: "No, you must answer each question to proceed. But don't worry - even if you're unsure, take your best guess! You won't lose points for wrong answers."
        },
        {
          question: 'Is there a time limit for each question?',
          answer: 'Regular questions have no time limit, so take your time! However, the bonus round does have a timer that affects your score multiplier - faster answers earn more points.'
        }
      ]
    },
    {
      icon: Trophy,
      title: 'Scoring & Leaderboards',
      faqs: [
        {
          question: 'How is my score calculated?',
          answer: 'You earn 100 points for each correct answer on regular questions. The bonus round can award additional points based on how quickly you answer correctly. Your total score is the sum of all points earned in a single game.'
        },
        {
          question: 'How do I get on the leaderboard?',
          answer: "To appear on the leaderboard, you need to create a nickname before playing. After completing a game, your score will automatically be recorded. The leaderboard shows daily, weekly, monthly, and all-time high scores."
        },
        {
          question: 'Can I change my nickname?',
          answer: 'Currently, nicknames cannot be changed once created. Choose wisely! If you need to use a different nickname, you can clear your browser data and register a new one.'
        },
        {
          question: 'What are the leaderboard time periods?',
          answer: 'We have four leaderboard categories: Daily (resets at midnight UTC), Weekly (resets Sunday midnight UTC), Monthly (resets on the 1st), and All-Time (never resets).'
        },
        {
          question: 'How do I see my ranking?',
          answer: "Click on the Leaderboard from the home page or your profile. You can see your ranking in the 'Your Stats' section, which shows your position among all players."
        }
      ]
    },
    {
      icon: Shield,
      title: 'Privacy & Data',
      faqs: [
        {
          question: 'Where does the mugshot data come from?',
          answer: 'All mugshots and arrest information come from publicly available records maintained by the Mecklenburg County Sheriff\'s Office in North Carolina. This is public information accessible to anyone.'
        },
        {
          question: 'Is my personal information safe?',
          answer: "We only collect the nickname you choose and your game scores. We don't require personal information like your real name or email to play. See our Privacy Policy for full details."
        },
        {
          question: 'Do you use cookies?',
          answer: 'Yes, we use cookies to remember your preferences, track game sessions, and for analytics. We also use third-party cookies for advertising through Google AdSense. You can manage cookies through your browser settings.'
        },
        {
          question: 'Can I delete my data?',
          answer: 'Yes! Contact us at privacy@jailbirdsgame.com to request deletion of your data, including your nickname and score history.'
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Legal & Content',
      faqs: [
        {
          question: 'Are these real mugshots?',
          answer: 'Yes, all mugshots shown are real images from actual arrests in Mecklenburg County, NC. They are public records obtained from the Sheriff\'s Office website.'
        },
        {
          question: 'Does being shown here mean someone is guilty?',
          answer: 'Absolutely not. An arrest does not mean guilt. All individuals shown are presumed innocent until proven guilty in a court of law. Charges may be dropped, reduced, or result in acquittal.'
        },
        {
          question: 'Is this game legal?',
          answer: 'Yes. We use publicly available information that anyone can access. Mugshots and arrest records are public in North Carolina under the Public Records Act.'
        },
        {
          question: 'What if I recognize someone in the game?',
          answer: "Please remember that arrest records are public information and an arrest doesn't equal guilt. If you have concerns about specific content, contact us at support@jailbirdsgame.com."
        },
        {
          question: 'Is this game appropriate for all ages?',
          answer: 'No. JailBirds is intended for users 18 years and older due to the nature of the content (criminal charges and mugshots). Please do not allow minors to play.'
        }
      ]
    }
  ];

  const handleToggle = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-8">
            <HelpCircle className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions about JailBirds
            </p>
          </div>

          {/* FAQ Categories */}
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <category.icon className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-heading font-bold">{category.title}</h2>
              </div>
              <div className="space-y-3">
                {category.faqs.map((faq, faqIndex) => (
                  <FAQItem
                    key={faqIndex}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openIndex === `${categoryIndex}-${faqIndex}`}
                    onClick={() => handleToggle(categoryIndex, faqIndex)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Still Need Help */}
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <BirdMascot size={48} className="mx-auto mb-4" />
            <h2 className="text-xl font-heading font-bold mb-2">Still Have Questions?</h2>
            <p className="text-muted-foreground mb-4">
              Can't find what you're looking for? We're here to help!
            </p>
            <Link to="/contact">
              <Button className="gap-2">
                Contact Support
              </Button>
            </Link>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;